/**
 * shop.vendor_payment_methods service layer.
 *
 * Generalizes the predecessor `vendorCryptoAddresses.ts` so a vendor
 * can publish ANY payment method as contact info on the public
 * storefront -- crypto networks plus custodial wallets (PayPal,
 * Alipay, Alibaba Pay, WeChat Pay, Cash App, Venmo, Zelle, Revolut,
 * Wise), bank transfer, and a free-text "other".
 *
 * V1 PeptideClients does NOT process payments and does NOT custody
 * funds. These rows are CONTACT INFO ONLY -- like a Telegram handle
 * or an email -- so a buyer viewing the vendor's public storefront
 * can copy the value (or open the vendor's URL) and pay the vendor
 * directly out-of-band. Every UI surface MUST reinforce that with a
 * `<SafetyCallout>`.
 *
 * Surfaces:
 *
 *   * apps/sell — vendor-team management page (list / add / edit /
 *     archive) backed by the SECURITY DEFINER RPCs.
 *   * apps/shop — public storefront read for the `/@<slug>` page,
 *     backed by `app_public.get_vendor_public_payment_methods`.
 *   * apps/sell + apps/shop — vendor-side composer attaches a payment
 *     method to a chat message via
 *     `shop.rpc_message_send_with_payment_method`. Buyers render the
 *     resulting attachment via the new
 *     `<PaymentMethodMessageCard>` in `@pc/shop-ui`.
 *
 * All write paths route through SECURITY DEFINER RPCs so the
 * suspension-lockdown helper (`shop.fn_assert_vendor_not_suspended`)
 * fires on every mutation. The table also has full-CRUD RLS for
 * vendor team members so a future bulk-edit screen can use plain
 * PostgREST writes if it wants to.
 */

import { supabase } from '../client';
import type { ShopMessage } from './shopTypes';

/**
 * Mirrors the `shop.payment_method_type_t` enum. Keep this list in
 * sync with the migration if a new value is added.
 */
export type PaymentMethodType =
  // Crypto
  | 'btc'
  | 'eth'
  | 'usdt_erc20'
  | 'usdt_trc20'
  | 'usdt_bep20'
  | 'usdc_erc20'
  | 'usdc_sol'
  | 'usdc_base'
  | 'sol'
  | 'ltc'
  | 'bch'
  | 'xmr'
  | 'doge'
  | 'matic'
  | 'bnb_bep20'
  | 'trx'
  // Custodial wallets / P2P
  | 'paypal'
  | 'alipay'
  | 'alibaba_pay'
  | 'wechat_pay'
  | 'zelle'
  | 'cashapp'
  | 'venmo'
  | 'revolut'
  | 'wise'
  // Bank transfer
  | 'bank_transfer'
  // Free-text
  | 'other';

/**
 * Friendly per-type label used by the management UI's <select>, the
 * public-storefront chip, and the in-message attachment card. Keeps
 * the labels canonical across surfaces.
 */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodType, string> = {
  btc: 'Bitcoin (BTC)',
  eth: 'Ethereum (ETH)',
  usdt_erc20: 'USDT · ERC-20',
  usdt_trc20: 'USDT · TRC-20',
  usdt_bep20: 'USDT · BEP-20',
  usdc_erc20: 'USDC · ERC-20',
  usdc_sol: 'USDC · Solana',
  usdc_base: 'USDC · Base',
  sol: 'Solana (SOL)',
  ltc: 'Litecoin (LTC)',
  bch: 'Bitcoin Cash (BCH)',
  xmr: 'Monero (XMR)',
  doge: 'Dogecoin (DOGE)',
  matic: 'Polygon (MATIC)',
  bnb_bep20: 'BNB · BEP-20',
  trx: 'Tron (TRX)',
  paypal: 'PayPal',
  alipay: 'Alipay',
  alibaba_pay: 'Alibaba Pay',
  wechat_pay: 'WeChat Pay',
  zelle: 'Zelle',
  cashapp: 'Cash App',
  venmo: 'Venmo',
  revolut: 'Revolut',
  wise: 'Wise',
  bank_transfer: 'Bank transfer',
  other: 'Other',
};

/** Coarse grouping used for UI sections + icons. */
export type PaymentMethodCategory = 'crypto' | 'wallet' | 'bank' | 'other';

const CRYPTO_TYPES: ReadonlySet<PaymentMethodType> = new Set<PaymentMethodType>([
  'btc',
  'eth',
  'usdt_erc20',
  'usdt_trc20',
  'usdt_bep20',
  'usdc_erc20',
  'usdc_sol',
  'usdc_base',
  'sol',
  'ltc',
  'bch',
  'xmr',
  'doge',
  'matic',
  'bnb_bep20',
  'trx',
]);

const WALLET_TYPES: ReadonlySet<PaymentMethodType> = new Set<PaymentMethodType>([
  'paypal',
  'alipay',
  'alibaba_pay',
  'wechat_pay',
  'zelle',
  'cashapp',
  'venmo',
  'revolut',
  'wise',
]);

/**
 * Methods where memo / destination tag / beneficiary reference is a
 * routine companion field. The sell-app UI shows the memo input
 * unconditionally for these and collapses it as advanced for the
 * rest. Strict per-network requirements vary so this is a UI hint
 * only -- the server never forces a memo.
 */
export const METHODS_WITH_MEMO: ReadonlySet<PaymentMethodType> = new Set<PaymentMethodType>(
  ['xmr', 'bank_transfer', 'other'],
);

/** Methods where a clickable URL is the primary action. */
export const METHODS_WITH_URL_PRIMARY: ReadonlySet<PaymentMethodType> = new Set<PaymentMethodType>(
  ['paypal', 'alipay', 'alibaba_pay', 'wechat_pay', 'revolut', 'wise'],
);

/** A row as the vendor-team management page sees it. */
export interface VendorPaymentMethod {
  id: string;
  vendor_id: string;
  method_type: PaymentMethodType;
  method_name_other: string | null;
  label: string;
  value: string;
  url: string | null;
  memo: string | null;
  is_active: boolean;
  sort_order: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

/** Slim row returned by the public storefront RPC (no audit columns). */
export interface PublicVendorPaymentMethod {
  id: string;
  method_type: PaymentMethodType;
  method_name_other: string | null;
  label: string;
  value: string;
  url: string | null;
  memo: string | null;
  notes: string | null;
  sort_order: number;
}

/**
 * Payment-method snapshot embedded into a `shop.messages.attachments`
 * entry by `shop.rpc_message_send_with_payment_method`. The backing
 * RPC versions the schema (`version: 1`) so future fields can be
 * added without breaking the buyer-side renderer.
 */
export interface PaymentMethodMessageAttachment {
  kind: 'payment_method';
  version: number;
  method_id: string;
  method_type: PaymentMethodType;
  method_name_other: string | null;
  label: string;
  value: string;
  url: string | null;
  memo: string | null;
  notes: string | null;
  snapshotted_at: string;
}

// =========================================================================
// Vendor-team RPCs (writes + read)
// =========================================================================

export interface CreateVendorPaymentMethodInput {
  vendorId: string;
  methodType: PaymentMethodType;
  /** Required when `methodType = 'other'`. Ignored otherwise. */
  methodNameOther?: string | null;
  label: string;
  value: string;
  url?: string | null;
  memo?: string | null;
  notes?: string | null;
  sortOrder?: number;
}

/**
 * Calls `shop.rpc_vendor_payment_method_create`. Returns the new row
 * id.
 *
 * Throws on:
 *   * vendor suspended (P0001 hint=vendor_suspended)
 *   * not authorized (42501)
 *   * length / required-field violation (22023)
 *   * (vendor_id, method_type, value) collision with an existing row (23505)
 */
export async function createVendorPaymentMethod(
  input: CreateVendorPaymentMethodInput,
): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_payment_method_create', {
      p_vendor_id: input.vendorId,
      p_method_type: input.methodType,
      p_method_name_other:
        input.methodType === 'other' ? (input.methodNameOther ?? null) : null,
      p_label: input.label,
      p_value: input.value,
      p_url: input.url ?? null,
      p_memo: input.memo ?? null,
      p_notes: input.notes ?? null,
      p_sort_order: input.sortOrder ?? 0,
    });
  if (error) throw error;
  return data as string;
}

export interface UpdateVendorPaymentMethodInput {
  id: string;
  /** When undefined the field is left as-is. */
  label?: string;
  /** When `null`, clears the URL. When undefined, leaves it as-is. */
  url?: string | null;
  /** When `null`, clears the memo. When undefined, leaves it as-is. */
  memo?: string | null;
  /** When `null`, clears the note. When undefined, leaves it as-is. */
  notes?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

/**
 * Calls `shop.rpc_vendor_payment_method_update`. method_type + value
 * are intentionally NOT updatable -- archive + recreate to rotate.
 */
export async function updateVendorPaymentMethod(
  input: UpdateVendorPaymentMethodInput,
): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_payment_method_update', {
      p_id: input.id,
      p_label: input.label ?? null,
      p_url: input.url === undefined ? null : input.url,
      p_memo: input.memo === undefined ? null : input.memo,
      p_notes: input.notes === undefined ? null : input.notes,
      p_sort_order: input.sortOrder ?? null,
      p_is_active: input.isActive ?? null,
    });
  if (error) throw error;
}

/**
 * Calls `shop.rpc_vendor_payment_method_archive`. Idempotent.
 */
export async function archiveVendorPaymentMethod(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_payment_method_archive', { p_id: id });
  if (error) throw error;
}

// =========================================================================
// Platform-admin write paths
// =========================================================================
//
// The vendor-team RPCs above (`rpc_vendor_payment_method_{create,update,
// archive}`) all gate on `shop.fn_is_vendor_user(p_vendor_id) OR
// core.is_platform_admin()` AND call `fn_assert_vendor_not_suspended`.
// That second check means an admin cannot fix a fraudulent payment-method
// URL on a suspended vendor via those RPCs without first un-suspending
// the vendor (which is the wrong workflow -- suspended vendors should
// stay suspended while we clean up their data).
//
// The admin verbs below take the direct-PostgREST path instead. RLS
// policy `vendor_payment_methods_admin_all` (migration
// `20260525150000_shop_vendor_payment_methods_generalize.sql`) grants
// FOR ALL TO authenticated USING core.is_platform_admin(), so admins
// can read, insert, update, and (logically) delete -- and the
// shop_vendor_payment_methods_audit trigger captures every mutation
// in core.audit_log with the admin's auth.uid().
//
// Use these from `apps/admin` only. From `apps/sell` (vendor team)
// keep using the SDF wrappers above so the suspension lockdown stays
// enforced for the vendor's own writes.

/**
 * Admin read of every payment-method row for a vendor (active +
 * archived). Direct PostgREST select against shop.vendor_payment_methods
 * so the admin sees archived history too -- useful when investigating
 * a complaint about a payment URL that was already taken down.
 */
export async function adminListVendorPaymentMethods(
  vendorId: string,
): Promise<VendorPaymentMethod[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_payment_methods')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('archived_at', { ascending: true, nullsFirst: true })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as VendorPaymentMethod[];
}

export interface AdminUpdateVendorPaymentMethodInput {
  id: string;
  /** When undefined, the field is left as-is. */
  label?: string;
  /** `null` clears the URL. `undefined` leaves it as-is. */
  url?: string | null;
  /** `null` clears the memo. `undefined` leaves it as-is. */
  memo?: string | null;
  /** `null` clears the notes. `undefined` leaves it as-is. */
  notes?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

/**
 * Admin-side update. Direct PostgREST update so it bypasses the
 * suspension-lockdown assertion the vendor-team RPC enforces. Server
 * CHECK constraints still validate length / value / URL shape.
 *
 * `method_type` and `value` are intentionally NOT in the input shape
 * (mirrors the rotation policy in
 * `shop.rpc_vendor_payment_method_update` -- silently rotating a
 * payment destination would history-revise the audit trail in a way
 * that defeats the audit chain). To rotate, archive the row and
 * create a new one.
 */
export async function adminUpdateVendorPaymentMethod(
  input: AdminUpdateVendorPaymentMethodInput,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.label !== undefined) patch.label = input.label;
  if (input.url !== undefined) patch.url = input.url;
  if (input.memo !== undefined) patch.memo = input.memo;
  if (input.notes !== undefined) patch.notes = input.notes;
  if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;
  if (input.isActive !== undefined) patch.is_active = input.isActive;
  if (Object.keys(patch).length === 0) return;

  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_payment_methods')
    .update(patch)
    .eq('id', input.id);
  if (error) throw error;
}

/**
 * Admin archive (soft-delete). Sets archived_at + flips is_active off.
 * Idempotent; calling on an already-archived row is a no-op.
 */
export async function adminArchiveVendorPaymentMethod(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_payment_methods')
    .update({ archived_at: new Date().toISOString(), is_active: false })
    .eq('id', id)
    .is('archived_at', null);
  if (error) throw error;
}

/**
 * Admin un-archive. Clears archived_at; does NOT automatically flip
 * is_active back on -- the admin re-activates separately so they
 * confirm the row is still valid before it surfaces on the public
 * storefront.
 */
export async function adminUnarchiveVendorPaymentMethod(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_payment_methods')
    .update({ archived_at: null })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Vendor-team list (active first, then archived). Backed by
 * `shop.rpc_vendor_payment_method_list_mine` so the call is gated on
 * `shop.fn_is_vendor_user(p_vendor_id)`.
 *
 * Result is intentionally not keyset-paginated: a vendor will list a
 * handful of methods (1-15). Same trade-off as the predecessor.
 */
export async function listVendorPaymentMethodsMine(
  vendorId: string,
): Promise<VendorPaymentMethod[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_payment_method_list_mine', { p_vendor_id: vendorId });
  if (error) throw error;
  return (data ?? []) as VendorPaymentMethod[];
}

// =========================================================================
// Public read (for the storefront)
// =========================================================================

/**
 * Calls `app_public.get_vendor_public_payment_methods(slug)` -- anon-
 * callable, no Supabase session required. Returns `[]` (NOT an error)
 * when the slug is unknown / archived / suspended / public_storefront_enabled
 * is false.
 */
export async function getPublicVendorPaymentMethods(
  slug: string,
): Promise<PublicVendorPaymentMethod[]> {
  const trimmed = slug.replace(/^@/, '').toLowerCase().trim();
  if (!trimmed) return [];
  const { data, error } = await (supabase() as any)
    .schema('app_public')
    .rpc('get_vendor_public_payment_methods', { p_slug: trimmed });
  if (error) throw error;
  return (data ?? []) as PublicVendorPaymentMethod[];
}

/**
 * Anon-friendly fetch helper for the public storefront context where
 * `initSupabase` has not been called (the storefront app intentionally
 * boots without a Supabase client). Calls the RPC over plain fetch
 * with the anon key, identical pattern to the existing edge-function
 * fetch in `apps/shop/src/pages/PublicStorefrontPage.tsx`.
 */
export async function getPublicVendorPaymentMethodsViaFetch(
  supabaseUrl: string,
  anonKey: string,
  slug: string,
): Promise<PublicVendorPaymentMethod[]> {
  const trimmed = slug.replace(/^@/, '').toLowerCase().trim();
  if (!trimmed) return [];
  const url = `${supabaseUrl}/rest/v1/rpc/get_vendor_public_payment_methods`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: anonKey,
      authorization: `Bearer ${anonKey}`,
      'accept-profile': 'app_public',
      'content-profile': 'app_public',
    },
    body: JSON.stringify({ p_slug: trimmed }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`payment-methods fetch failed (${resp.status}): ${text}`);
  }
  const json = (await resp.json()) as PublicVendorPaymentMethod[] | null;
  return json ?? [];
}

// =========================================================================
// Vendor-only message attachment send
// =========================================================================

export interface SendMessageWithPaymentMethodInput {
  threadId: string;
  body: string;
  paymentMethodId: string;
}

/**
 * Calls `shop.rpc_message_send_with_payment_method`. Vendor-team
 * only. Returns the newly inserted `shop.messages` row (with the
 * attachment payload embedded in `attachments`).
 */
export async function sendMessageWithPaymentMethod(
  input: SendMessageWithPaymentMethodInput,
): Promise<ShopMessage> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_message_send_with_payment_method', {
      p_thread_id: input.threadId,
      p_body: input.body,
      p_payment_method_id: input.paymentMethodId,
    });
  if (error) throw error;
  return data as ShopMessage;
}

// =========================================================================
// UI helpers
// =========================================================================

/**
 * Friendly chip text for a row. Falls through `method_name_other` for
 * the `'other'` type, returning the vendor-supplied free-text name.
 */
export function formatMethodLabel(
  type: PaymentMethodType,
  methodNameOther?: string | null,
): string {
  if (type === 'other') {
    const name = (methodNameOther ?? '').trim();
    return name.length > 0 ? name : PAYMENT_METHOD_LABELS.other;
  }
  return PAYMENT_METHOD_LABELS[type];
}

/** Coarse category used for UI grouping + icon selection. */
export function methodCategory(type: PaymentMethodType): PaymentMethodCategory {
  if (CRYPTO_TYPES.has(type)) return 'crypto';
  if (WALLET_TYPES.has(type)) return 'wallet';
  if (type === 'bank_transfer') return 'bank';
  return 'other';
}

/** Whether this method type carries a meaningful primary `value`. */
export function methodNeedsValue(_type: PaymentMethodType): boolean {
  // Every supported method has a primary identifier today (an address,
  // a handle, an email, an account number, a reference). Future
  // method types might have only a URL; flip the return for those.
  return true;
}

/** Whether the URL field is the primary action surface for this type. */
export function methodNeedsUrl(type: PaymentMethodType): boolean {
  return METHODS_WITH_URL_PRIMARY.has(type);
}

/** Whether memo / destination tag is a routine companion. */
export function methodSupportsMemo(type: PaymentMethodType): boolean {
  return METHODS_WITH_MEMO.has(type);
}

/**
 * Truncate-middle helper -- "TXyZ…7n9q". Used to render long values
 * (crypto addresses, IBANs) on small screens while keeping head + tail
 * visible for sanity-checking before the buyer copies.
 */
export function truncateValueMiddle(
  raw: string,
  options: { headChars?: number; tailChars?: number } = {},
): string {
  const head = options.headChars ?? 6;
  const tail = options.tailChars ?? 4;
  const min = head + tail + 1;
  if (raw.length <= min) return raw;
  return `${raw.slice(0, head)}…${raw.slice(-tail)}`;
}

// =========================================================================
// Back-compat aliases. The predecessor module exported these names; we
// preserve them as deprecated re-exports so the rename can land in a
// single PR without coordinating a flag-day across other packages.
// =========================================================================

/** @deprecated Renamed to {@link PaymentMethodType}. */
export type CryptoNetwork = PaymentMethodType;
/** @deprecated Renamed to {@link VendorPaymentMethod}. */
export type VendorCryptoAddress = VendorPaymentMethod;
/** @deprecated Renamed to {@link PublicVendorPaymentMethod}. */
export type PublicVendorCryptoAddress = PublicVendorPaymentMethod;

/** @deprecated Use {@link PAYMENT_METHOD_LABELS}. */
export const CRYPTO_NETWORK_LABELS = PAYMENT_METHOD_LABELS;

/** @deprecated Use {@link METHODS_WITH_MEMO}. */
export const NETWORKS_WITH_MEMO = METHODS_WITH_MEMO;

/** @deprecated Use {@link formatMethodLabel}. */
export const formatNetworkLabel = formatMethodLabel;

/** @deprecated Use {@link truncateValueMiddle}. */
export const truncateAddressMiddle = truncateValueMiddle;

/** @deprecated Use {@link getPublicVendorPaymentMethodsViaFetch}. */
export const getPublicVendorCryptoAddressesViaFetch =
  getPublicVendorPaymentMethodsViaFetch;
